// Google Analytics Event Tracking Utility
// This utility helps track conversions and user interactions

/**
 * Track a custom event in Google Analytics
 * @param {string} eventName - Name of the event (e.g., 'form_submission', 'button_click')
 * @param {object} eventParams - Additional event parameters
 */
export const trackEvent = (eventName, eventParams = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, {
      ...eventParams,
      // Add timestamp for debugging
      event_timestamp: new Date().toISOString()
    });
  } else {
    // Fallback for development
    console.log('GA Event:', eventName, eventParams);
  }
};

/**
 * Track form submission (lead generation)
 * @param {string} formType - Type of form (e.g., 'contact', 'newsletter')
 * @param {object} formData - Form data (optional, for debugging)
 */
export const trackFormSubmission = (formType, formData = {}) => {
  trackEvent('form_submission', {
    event_category: 'Lead Generation',
    event_label: formType,
    value: 1, // Each form submission is worth 1 conversion
    form_type: formType,
    service_interest: formData.serviceInterest || 'not_specified'
  });

  // Also track as conversion
  trackEvent('generate_lead', {
    currency: 'USD',
    value: 1,
    form_type: formType
  });
};

/**
 * Track CTA button clicks
 * @param {string} buttonText - Text on the button
 * @param {string} location - Where the button is located (e.g., 'hero', 'footer')
 * @param {string} destination - Where the button leads (e.g., '/contact', '/services')
 */
export const trackCTAClick = (buttonText, location, destination) => {
  trackEvent('cta_click', {
    event_category: 'Engagement',
    event_label: buttonText,
    button_location: location,
    button_destination: destination,
    value: 0.5 // Partial conversion value
  });
};

/**
 * Track email link clicks
 * @param {string} email - Email address clicked
 * @param {string} location - Where the email link is located
 */
export const trackEmailClick = (email, location) => {
  trackEvent('email_click', {
    event_category: 'Contact',
    event_label: email,
    email_location: location
  });
};

/**
 * Track external link clicks (outbound)
 * @param {string} url - URL being clicked
 * @param {string} linkText - Text of the link
 */
export const trackOutboundClick = (url, linkText) => {
  trackEvent('outbound_click', {
    event_category: 'Outbound',
    event_label: url,
    link_text: linkText
  });
};

/**
 * Track service interest selection
 * @param {string} service - Service selected
 */
export const trackServiceInterest = (service) => {
  trackEvent('service_interest', {
    event_category: 'Engagement',
    event_label: service,
    value: 0.3 // Lower value than form submission
  });
};

/**
 * Track page view (for SPA routing)
 * @param {string} pagePath - Path of the page
 * @param {string} pageTitle - Title of the page
 */
export const trackPageView = (pagePath, pageTitle) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', 'G-EC87L5RBB6', {
      page_path: pagePath,
      page_title: pageTitle
    });
  }
};

/**
 * Track scroll depth (for engagement)
 * @param {number} depth - Scroll depth percentage (25, 50, 75, 100)
 */
export const trackScrollDepth = (depth) => {
  trackEvent('scroll_depth', {
    event_category: 'Engagement',
    event_label: `${depth}%`,
    value: depth / 100
  });
};

/**
 * Track time on page
 * @param {number} seconds - Time spent on page in seconds
 */
export const trackTimeOnPage = (seconds) => {
  if (seconds >= 30) { // Only track if user spent significant time
    trackEvent('time_on_page', {
      event_category: 'Engagement',
      event_label: `${Math.floor(seconds)}s`,
      value: Math.min(seconds / 60, 1) // Max value of 1 for 60+ seconds
    });
  }
};

